import { useMemo } from 'react'
import { getColor, getActivitiesWithAnswers } from '../helpers/activities_helper'
import infoDataTypeMap from '../components/InformationalActivities/infoDataTypeMap'
import { ACTIVITY_TYPES } from '../helpers/activity_types'
import { ACTIVITY_GROUP_MODES } from '../constants/activityGroupModes'

const castToInt = (value) => (typeof value === 'number' ? value : Number.parseInt(value, 10))

// Derives enriched activity group data and related computed values
export default function useActivityGroupDerivations({
  activityGroupData,
  person,
  activitiesData,
  state,
  pastAnswersData,
}) {
  const activityGroupEnriched = useMemo(() => {
    if (!activityGroupData) return null
    const activityGroupColorToUse = getColor({ person, activityGroup: activityGroupData })
    const expanded = { ...activityGroupData, activityGroupColor: activityGroupColorToUse }
    if (person) expanded.person = person
    return expanded
  }, [activityGroupData, person])

  const activitiesComputed = useMemo(() => {
    if (!activityGroupEnriched) return []
    if (!activityGroupEnriched?.infoDataType && activitiesData?.items?.length > 0) {
      const items = activitiesData?.items ?? []
      const filtered =
        activityGroupEnriched?.dataType === 'ranking' || activityGroupEnriched?.dataType === 'top-10'
          ? items.filter((activity) => activity?.type !== ACTIVITY_TYPES.FINISH)
          : items
      return filtered
    }
    return infoDataTypeMap[activityGroupEnriched?.infoDataType] ?? []
  }, [activitiesData, activityGroupEnriched])

  /**
   * Determines whether to reuse the fetched (past) answers for the activity group.
   *
   * Business rules:
   * - Outside of INPUT mode (viewing/reviewing) → always use the fetched answer.
   * - In INPUT mode → only reuse the fetched answer when its id matches the answer being edited.
   *   A brand-new or cross-user input session (no id yet, or a mismatched id) stays a clean slate,
   *   so a foreign completed answer can never hijack the session into REVIEW (DSC-9347).
   *
   * @param {object|null} answersData - The previously fetched answers data, if any.
   * @param {object} stateObj - The current state, containing activityGroupMode and activityGroupAnswerId.
   * @returns {boolean} True if past answers should be used, false otherwise.
   */
  function shouldUsePastAnswersFn(answersData, stateObj) {
    if (!answersData) return false

    // When not in an input session we are viewing/reviewing, so always use the fetched answer.
    const isInputMode = stateObj?.activityGroupMode === ACTIVITY_GROUP_MODES.INPUT
    if (!isInputMode) return true

    // In an input session, only inherit a fetched answer that is actually the one being edited.
    // This covers "Start Activity" from another user's completed memory (DSC-9347): the route still
    // points at that user's completed answer, so it keeps getting fetched — but once the current user
    // starts their own answer (a new/blank or different answer id) we must NOT pull in the other
    // person's answer, even though it has a completeDate. A brand-new session has no answer id yet
    // (or a mismatched one) and must stay a clean slate.
    const currentAnswerId = castToInt(stateObj?.activityGroupAnswerId)
    const fetchedAnswerId = castToInt(answersData?.id)

    if (Number.isNaN(currentAnswerId) || Number.isNaN(fetchedAnswerId)) return false

    return fetchedAnswerId === currentAnswerId
  }

  const shouldUsePastAnswers = shouldUsePastAnswersFn(pastAnswersData, state)
  const selectedSavedActivityGroupAnswers = shouldUsePastAnswers ? pastAnswersData : null

  // Determine derived mode. Only force REVIEW for a completed answer we are actually going to
  // display (selectedSavedActivityGroupAnswers already excludes a fetched answer whose id does not
  // match the one being edited — see shouldUsePastAnswersFn). This keeps "click a completed answer
  // from the list" opening in REVIEW even when state mode is briefly stale, while preventing another
  // person's completed answer from hijacking a fresh "Start Activity" input session (DSC-9347).
  const activityGroupModeDerived = useMemo(() => {
    if (selectedSavedActivityGroupAnswers?.completeDate) return ACTIVITY_GROUP_MODES.REVIEW
    // No fetched completed answer to display; fall back to state mode or INPUT if using past answers
    if (!selectedSavedActivityGroupAnswers) return state?.activityGroupMode
    return ACTIVITY_GROUP_MODES.INPUT
  }, [selectedSavedActivityGroupAnswers, state?.activityGroupMode])

  const activityGroupAnswerIdDerived = selectedSavedActivityGroupAnswers?.id ?? state?.activityGroupAnswerId

  const activityGroupAnswersDerived = state?.activityGroupAnswers ?? selectedSavedActivityGroupAnswers

  const selectedActivitiesWithAnswersDerived = useMemo(() => {
    if (!activitiesComputed?.length || !activityGroupAnswersDerived) return []
    return getActivitiesWithAnswers({
      activities: activitiesComputed,
      selectedSavedActivityGroupAnswers: activityGroupAnswersDerived,
    })
  }, [activitiesComputed, activityGroupAnswersDerived])

  const memoryArtifactDerived = useMemo(() => {
    if (!shouldUsePastAnswers) return state?.memoryArtifact
    const mem = pastAnswersData?.memoryArtifact
    if (!mem) return state?.memoryArtifact
    return {
      ...mem,
      createDate: pastAnswersData?.createDate,
      completeDate: pastAnswersData?.completeDate,
      authorCisId: pastAnswersData?.cisUserId,
      answerId: pastAnswersData?.id,
      grouping: pastAnswersData?.grouping ?? null,
    }
  }, [shouldUsePastAnswers, pastAnswersData, state?.memoryArtifact])

  return {
    activityGroupEnriched,
    activitiesComputed,
    activityGroupModeDerived,
    activityGroupAnswerIdDerived,
    activityGroupAnswersDerived,
    selectedActivitiesWithAnswersDerived,
    memoryArtifactDerived,
  }
}
